import {
  BATTLE_ROOM_RUNTIME_OVERLAY_KEYS,
  BATTLE_ROOM_VISUAL_STATE_KEYS,
  buildSnapshotMetadata,
  extractSnapshotEntityReference,
  normalizeSnapshotEntityType,
  normalizeSnapshotLayerId,
  pickDefinedEntries
} from './BattleRoomSnapshotContract';

const cloneValue = (value) => {
  if (value === undefined) return undefined;
  try {
    return JSON.parse(JSON.stringify(value));
  } catch (_) {
    return value;
  }
};

const stripEmbeddedEntityDuplication = (tokenClone) => {
  if (!tokenClone || typeof tokenClone !== 'object') {
    return {};
  }

  const tokenWithoutEmbeddedEntities = { ...tokenClone };
  const duplicatedKeys = [
    ...BATTLE_ROOM_RUNTIME_OVERLAY_KEYS,
    ...BATTLE_ROOM_VISUAL_STATE_KEYS,
    'characterData',
    'monsterData',
    'schemaData',
    'canonicalData',
    'originalUnifiedEntity',
    'data'
  ];

  duplicatedKeys.forEach((key) => {
    delete tokenWithoutEmbeddedEntities[key];
  });

  return tokenWithoutEmbeddedEntities;
};

const toEntityPayload = (token) => {
  if (!token || typeof token !== 'object') return null;

  const entityType = normalizeSnapshotEntityType(token.type);
  const embeddedPayload = entityType === 'monster'
    ? (token.monsterData || token.characterData)
    : (token.characterData || token.monsterData);

  return embeddedPayload && typeof embeddedPayload === 'object'
    ? cloneValue(embeddedPayload)
    : null;
};

export const extractSnapshotRuntimeOverlay = (token) => ({
  ...pickDefinedEntries(token, BATTLE_ROOM_RUNTIME_OVERLAY_KEYS),
  layer: normalizeSnapshotLayerId(token?.layer)
});

export const extractSnapshotVisualState = (token) => ({
  ...pickDefinedEntries(token, BATTLE_ROOM_VISUAL_STATE_KEYS),
  layer: normalizeSnapshotLayerId(token?.layer)
});

export const serializeTokenSnapshot = (token) => {
  const tokenClone = cloneValue(token);
  const entityType = normalizeSnapshotEntityType(token?.type);
  const entityPayload = toEntityPayload(token);
  const runtimeOverlay = extractSnapshotRuntimeOverlay(tokenClone);
  const visualState = extractSnapshotVisualState(tokenClone);
  const metadata = buildSnapshotMetadata(tokenClone);
  const tokenWithoutEmbeddedEntities = stripEmbeddedEntityDuplication(tokenClone);

  return {
    ...tokenWithoutEmbeddedEntities,
    ...metadata,
    // Keep the per-instance identity at the compact record root. Visual state
    // also carries it for rendering compatibility, but authoritative server
    // mutations address map.tokens[].id directly. date_of_change: 2026-08-25
    id: tokenClone.id,
    type: entityType,
    layer: visualState.layer,
    runtimeOverlay,
    visualState,
    entityPayload
  };
};

export const buildTemplateSnapshotKey = (tokenSnapshot) => {
  const entityRef = extractSnapshotEntityReference(tokenSnapshot);
  return entityRef.entityId || entityRef.canonicalId || tokenSnapshot?.baseTemplate || tokenSnapshot?.id || null;
};

export const serializeBattleRoomTokenCollection = (tokens) => {
  const serializedTokens = [];
  const templateMap = {};

  (Array.isArray(tokens) ? tokens : []).forEach((token) => {
    const snapshot = serializeTokenSnapshot(token);
    // Payloads belong to instances. Sharing by monsterId discarded independent
    // edits when another copy saved later. date_of_change: 2026-09-05
    const templateKey = snapshot.id ? `instance:${snapshot.id}` : buildTemplateSnapshotKey(snapshot);
    if (templateKey && snapshot.entityPayload) {
      templateMap[templateKey] = snapshot.entityPayload;
      if (normalizeSnapshotEntityType(snapshot?.type) === 'monster') {
        snapshot.monsterDataRef = templateKey;
      } else {
        snapshot.characterDataRef = templateKey;
      }
      snapshot.entityPayload = undefined;
    }

    serializedTokens.push(snapshot);
  });

  return {
    tokens: serializedTokens,
    templateMap
  };
};

const BattleRoomSnapshotSerializer = {
  extractSnapshotRuntimeOverlay,
  extractSnapshotVisualState,
  serializeTokenSnapshot,
  buildTemplateSnapshotKey,
  serializeBattleRoomTokenCollection
};

export default BattleRoomSnapshotSerializer;
